import { useEffect, useRef } from "react";

const HOVER_SELECTOR = "a, button, .work-row, .tag, .ed-btn";

/**
 * Custom dot cursor with a lag/lerp follow and a "big" state over interactive
 * elements. Fixes the common browser bug where the dot freezes or stays stuck
 * after leaving the window / switching tabs: it hides on mouseleave, window
 * blur, and tab hide, and re-syncs position on mouseenter / focus / tab show.
 */
export function Cursor() {
  const curRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cur = curRef.current;
    if (!cur) return;

    let mx = window.innerWidth / 2;
    let my = window.innerHeight / 2;
    let cx = mx;
    let cy = my;
    let raf = 0;

    const setPos = () => {
      cur.style.transform = `translate(calc(${cx}px - 50%), calc(${cy}px - 50%))`;
    };

    const onMove = (e: MouseEvent) => {
      mx = e.clientX;
      my = e.clientY;
    };

    // Delegated hover detection — survives dynamically rendered content.
    const onOver = (e: MouseEvent) => {
      if ((e.target as Element)?.closest?.(HOVER_SELECTOR)) cur.classList.add("big");
    };
    const onOut = (e: MouseEvent) => {
      const to = e.relatedTarget as Element | null;
      if (!to || !to.closest?.(HOVER_SELECTOR)) cur.classList.remove("big");
    };

    // ── Visibility bug fix ──
    const hide = () => {
      cur.style.opacity = "0";
    };
    const showAt = (x: number, y: number) => {
      mx = cx = x;
      my = cy = y;
      setPos();
      cur.style.opacity = "1";
    };
    const onDocLeave = () => hide();
    const onDocEnter = (e: MouseEvent) => showAt(e.clientX, e.clientY);
    const onBlur = () => hide();
    const onFocus = () => {
      cx = mx;
      cy = my;
      cur.style.opacity = "1";
    };
    const onVisibility = () => {
      cur.style.opacity = document.hidden ? "0" : "1";
    };

    const loop = () => {
      cx += (mx - cx) * 0.2;
      cy += (my - cy) * 0.2;
      setPos();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseover", onOver);
    document.addEventListener("mouseout", onOut);
    document.addEventListener("mouseleave", onDocLeave);
    document.addEventListener("mouseenter", onDocEnter);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
      document.removeEventListener("mouseleave", onDocLeave);
      document.removeEventListener("mouseenter", onDocEnter);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  return <div className="cur" ref={curRef} aria-hidden="true" />;
}
